import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Stack, Step, StepButton, Stepper, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import {
  getGetAnalysisQuestionnaireQueryKey,
  useEvaluateAnalysisQuestionnaire,
  useSaveAnalysisQuestionnaireAnswer,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { EvaluateBadRequestErrorCode } from "Infrastructure/ApiZfp/Schema";
import type { QuestionnaireAnswerItemDto } from "Infrastructure/ApiZfp/Schema";
import { Configuration } from "Infrastructure/Config/config";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getCzechPluralForm } from "Infrastructure/Utils/LanguageUtils";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { QuestionnaireChangeReasonModal } from "./QuestionnaireChangeReasonModal";
import { useQuestionnaireContext } from "./QuestionnaireContext";
import { QuestionnaireStep } from "./QuestionnaireStep";
import { getQuestionnaireStepSchema } from "./questionnaireSchema";
import { computeRuleState } from "./useRuleEngine";
import {
  generateRandomAnswers,
  haveAnswersChanged,
  mapAnswerPayloadToFormData,
  mapFormDataToAnswerPayload,
  mergeFormData,
  pruneHiddenAnswers,
} from "./utils";

// Choice questions are marked by QuestionnaireQuestion, text inputs mark themselves through aria-invalid.
const INVALID_ANSWER_SELECTOR = '[data-question-error="true"], [aria-invalid="true"]';
const FOCUSABLE_SELECTOR = 'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), textarea:not([disabled]), [tabindex="0"]';

type Props = {
  initialAnswerItems?: QuestionnaireAnswerItemDto[];
  onEvaluated: () => void;
  requiresChangeReason?: boolean;
  hasUnevaluatedChanges?: boolean;
};

export const QuestionnaireForm = ({
  initialAnswerItems = [],
  onEvaluated,
  requiresChangeReason = false,
  hasUnevaluatedChanges = false,
}: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { allQuestions, groups, investmentPlans, isReadOnly } = useQuestionnaireContext();
  const queryClient = useQueryClient();
  const { mutateAsync: saveAnswers } = useSaveAnalysisQuestionnaireAnswer();
  const { mutateAsync: makeEvaluation } = useEvaluateAnalysisQuestionnaire();
  const [currentStep, setCurrentStep] = useState(0);
  const [collectedData, setCollectedData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const stepperRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isChangeReasonModalOpen, setIsChangeReasonModalOpen] = useState(false);
  // Counted rather than a flag, so every further attempt re-runs the scroll below even when the hint is already up.
  const [failedAttempts, setFailedAttempts] = useState(0);
  const pendingSubmissionRef = useRef<{ newCollectedData: Record<string, any>; prunedData: Record<string, any> } | null>(null);
  const isDevelopment = Configuration.getIsDevelopment();
  const showTestButton = isDevelopment && !isReadOnly;
  const initialFormData = useMemo(() => mapAnswerPayloadToFormData(initialAnswerItems), [initialAnswerItems]);

  const dynamicResolver = async (data: any, context: any, options: any) => {
    const currentRuleState = computeRuleState(allQuestions, collectedData, data);
    const dynamicSchema = getQuestionnaireStepSchema(currentGroupQuestions, allQuestions, t, currentRuleState, investmentPlans);
    const yupRes = yupResolver(dynamicSchema);
    return yupRes(data, context, options);
  };

  const formValues = useMemo(
    () => ({ ...mapAnswerPayloadToFormData(initialAnswerItems), ...collectedData }),
    [initialAnswerItems, collectedData],
  );

  const methods = useForm({
    mode: "onChange",
    resolver: dynamicResolver,
    // The failed-attempt effect moves the focus in DOM order; RHF would follow its own field registration order instead.
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: formValues,
    values: formValues,
  });

  const formData = useWatch({ control: methods.control });
  const globalRuleState = useMemo(() => computeRuleState(allQuestions, formValues, formData), [allQuestions, formValues, formData]);

  const steps = useMemo(
    () => groups.filter((group) => globalRuleState.isGroupVisible(group.questionnaireGroupID)),
    [groups, globalRuleState],
  );

  const safeStepIndex = steps.length > 0 ? Math.min(currentStep, steps.length - 1) : 0;
  const currentGroup = steps.length > 0 ? steps[safeStepIndex] : undefined;
  const isLastQuestionnaireStep = steps.length > 0 && safeStepIndex === steps.length - 1;
  const missingAnswersCount = Object.keys(methods.formState.errors).length;
  const showStepValidationError = failedAttempts > 0 && missingAnswersCount > 0;

  const currentGroupQuestions = useMemo(() => {
    if (!currentGroup) return [];
    return allQuestions.filter((q) => q.questionnaireGroupID === currentGroup.questionnaireGroupID);
  }, [allQuestions, currentGroup]);

  useEffect(() => {
    if (failedAttempts === 0) return;
    const firstInvalid = formRef.current?.querySelector<HTMLElement>(INVALID_ANSWER_SELECTOR);
    if (!firstInvalid) return;
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusTarget = firstInvalid.matches(FOCUSABLE_SELECTOR)
      ? firstInvalid
      : firstInvalid.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    focusTarget?.focus({ preventScroll: true });
  }, [failedAttempts]);

  const handleNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    const isStepValid = await methods.trigger();
    setFailedAttempts((count) => (isStepValid ? 0 : count + 1));

    if (isStepValid) {
      const currentStepData = methods.getValues();
      const newCollectedData = mergeFormData(collectedData, currentStepData);
      const mergedData = mergeFormData(formValues, newCollectedData);

      setIsSubmitting(true);
      try {
        if (!isReadOnly) {
          await saveAnswers({
            analysisId,
            versionId,
            data: { answers: mapFormDataToAnswerPayload(mergedData) },
          });
        }

        setCollectedData(newCollectedData);
        setCurrentStep((prev) => prev + 1);
        stepperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    } else {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const submitEvaluation = async (newCollectedData: Record<string, any>, prunedData: Record<string, any>, reason?: string) => {
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (!isReadOnly) {
        await saveAnswers({
          analysisId,
          versionId,
          data: { answers: mapFormDataToAnswerPayload(prunedData) },
        });

        setCollectedData(newCollectedData);
        await makeEvaluation({ analysisId, versionId, data: reason ? { reason } : {} });
        await queryClient.invalidateQueries({ queryKey: getGetAnalysisQuestionnaireQueryKey(analysisId, versionId) });
      }
      onEvaluated();
    } catch (error) {
      if ((error as any)?.code === EvaluateBadRequestErrorCode.evaluation_reason_required) {
        pendingSubmissionRef.current = { newCollectedData, prunedData };
        setIsChangeReasonModalOpen(true);
      } else {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (isSubmittingRef.current) return;
    const newCollectedData = mergeFormData(collectedData, data);
    const mergedData = mergeFormData(formValues, newCollectedData);
    const prunedData = pruneHiddenAnswers(allQuestions, investmentPlans, mergedData);

    if (requiresChangeReason && (hasUnevaluatedChanges || haveAnswersChanged(initialFormData, prunedData))) {
      pendingSubmissionRef.current = { newCollectedData, prunedData };
      setIsChangeReasonModalOpen(true);
      return;
    }

    await submitEvaluation(newCollectedData, prunedData);
  };

  const handleChangeReasonConfirm = async (reason: string) => {
    setIsChangeReasonModalOpen(false);
    const pending = pendingSubmissionRef.current;
    pendingSubmissionRef.current = null;
    if (pending) await submitEvaluation(pending.newCollectedData, pending.prunedData, reason);
  };

  const handleTestFill = () => {
    const answers = generateRandomAnswers(allQuestions, currentGroupQuestions, investmentPlans, collectedData);
    const applyAnswers = () => {
      for (const [key, value] of Object.entries(answers)) {
        methods.setValue(key, value, { shouldDirty: true, shouldTouch: true });
      }
    };
    applyAnswers();
    // A value for a field that a chosen answer only reveals (e.g. the % under an ESG option) is set before the field
    // mounts, and with shouldUnregister the field's unmount in StrictMode's double mount drops it again, while the input
    // still shows it. So the answers go in once more after the revealed fields have mounted.
    setTimeout(applyAnswers);
  };

  const handleStepClick = (targetStepIndex: number, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (isReadOnly || targetStepIndex < safeStepIndex) {
      setCurrentStep(targetStepIndex);
    }
  };

  return (
    <>
      <Stepper
        ref={stepperRef}
        activeStep={safeStepIndex}
        alternativeLabel
        sx={{
          mb: 4,
          "& .MuiStepIcon-root": { color: "text.secondary", "&.Mui-active, &.Mui-completed": { color: "primary.main" } },
          "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
            borderColor: "primary.main",
          },
          "& .MuiStepLabel-label": { "&, &.Mui-completed": { fontWeight: 400 }, "&.Mui-active": { fontWeight: 700 } },
        }}
      >
        {steps.map((group, index) => (
          <Step key={group.questionnaireGroupID} disabled={!isReadOnly && index >= safeStepIndex}>
            <StepButton onClick={() => handleStepClick(index)}>{group.name}</StepButton>
          </Step>
        ))}
      </Stepper>
      <FormProvider {...methods}>
        <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit, () => setFailedAttempts((count) => count + 1))}>
          <Box sx={{ minHeight: 200, mb: 4 }}>
            {currentGroup != null && (
              <QuestionnaireStep
                questions={allQuestions}
                currentGroup={currentGroup}
                investmentPlans={investmentPlans}
                shouldShowInitBlock={currentStep === 0}
                collectedData={formValues}
                stepIndex={safeStepIndex}
                footer={
                  <Stack px={Spacing.XXL} mt={Spacing.XXXL} spacing={Spacing.M}>
                    <Flex justifyContent={showTestButton ? "space-between" : "flex-end"} alignItems="center">
                      {showTestButton && (
                        <Button type="button" onClick={handleTestFill} variant="outlined" color="secondary" size="small">
                          Test vyplnění
                        </Button>
                      )}
                      {!isLastQuestionnaireStep ? (
                        <Button type="button" onClick={handleNext} variant="contained" disabled={isSubmitting}>
                          {t(Resources.Common.Continue)}
                        </Button>
                      ) : isReadOnly ? (
                        <Button type="button" onClick={onEvaluated} variant="contained">
                          {t(Resources.Questionnaire.Evaluation.ShowEvaluation)}
                        </Button>
                      ) : (
                        <Button type="submit" variant="contained" disabled={isSubmitting}>
                          {t(Resources.Questionnaire.Evaluate)}
                        </Button>
                      )}
                    </Flex>
                    {/* Always rendered, only hidden - otherwise the footer would change height and shift the page. */}
                    <Flex
                      justifyContent="flex-end"
                      alignItems="center"
                      gap={Spacing.S}
                      color="error.main"
                      visibility={showStepValidationError ? "visible" : "hidden"}
                    >
                      <ErrorIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" fontWeight={500} color="inherit">
                        {t(
                          getCzechPluralForm(missingAnswersCount, {
                            one: Resources.Questionnaire.Validation.MissingAnswersOne,
                            few: Resources.Questionnaire.Validation.MissingAnswersFew,
                            other: Resources.Questionnaire.Validation.MissingAnswersOther,
                          }),
                          { count: missingAnswersCount },
                        )}
                      </Typography>
                    </Flex>
                  </Stack>
                }
              />
            )}
          </Box>
        </form>
      </FormProvider>
      <QuestionnaireChangeReasonModal
        isOpen={isChangeReasonModalOpen}
        onClose={() => setIsChangeReasonModalOpen(false)}
        onConfirm={handleChangeReasonConfirm}
      />
    </>
  );
};
